// src/app/core/ui/theme/ThemeProvider.tsx
import React, { createContext, useContext, useEffect, useState, useCallback, useLayoutEffect } from 'react';

type Theme = 'light' | 'dark' | 'auto';

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

const lightThemeVars: Record<string, string> = {
  '--tg-theme-bg-color': '#ffffff',
  '--tg-theme-text-color': '#000000',
  '--tg-theme-hint-color': '#999999',
  '--tg-theme-link-color': '#2481cc',
  '--tg-theme-button-color': '#2481cc',
  '--tg-theme-button-text-color': '#ffffff',
  '--tg-theme-secondary-bg-color': '#f4f4f5',
  '--tg-theme-section-bg-color': '#ffffff',
  '--background': '#ffffff',
  '--foreground': '#000000',
  '--card': '#ffffff',
  '--card-foreground': '#000000',
  '--muted': '#f4f4f5',
  '--muted-foreground': '#999999',
  '--accent': '#2481cc',
  '--accent-foreground': '#ffffff',
  '--border': 'rgba(0, 0, 0, 0.08)',
  '--tg-bg-color': '#ffffff',
  '--tg-text-color': '#000000',
  '--tg-button-color': '#2481cc',
  '--tg-secondary-bg-color': '#f4f4f5',
  '--shadow': '0 -4px 12px rgba(0, 0, 0, 0.06)',
};

const darkThemeVars: Record<string, string> = {
  '--tg-theme-bg-color': '#1a1a1a',
  '--tg-theme-text-color': '#ffffff',
  '--tg-theme-hint-color': '#aaaaaa',
  '--tg-theme-link-color': '#5a9cff',
  '--tg-theme-button-color': '#5a9cff',
  '--tg-theme-button-text-color': '#ffffff',
  '--tg-theme-secondary-bg-color': '#2a2a2a',
  '--tg-theme-section-bg-color': '#1a1a1a',
  '--background': '#1a1a1a',
  '--foreground': '#ffffff',
  '--card': '#1a1a1a',
  '--card-foreground': '#ffffff',
  '--muted': '#2a2a2a',
  '--muted-foreground': '#aaaaaa',
  '--accent': '#5a9cff',
  '--accent-foreground': '#ffffff',
  '--border': 'rgba(255, 255, 255, 0.12)',
  '--tg-bg-color': '#1a1a1a',
  '--tg-text-color': '#ffffff',
  '--tg-button-color': '#5a9cff',
  '--tg-secondary-bg-color': '#2a2a2a',
  '--shadow': '0 -8px 20px rgba(0, 0, 0, 0.4)',
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    const saved = localStorage.getItem('app-theme');
    return (saved as Theme) || 'auto';
  });

  const getResolvedTheme = useCallback((currentTheme: Theme): 'light' | 'dark' => {
    if (currentTheme !== 'auto') return currentTheme;
    const tgColorScheme = (window as any).Telegram?.WebApp?.colorScheme;
    if (tgColorScheme === 'dark') return 'dark';
    if (tgColorScheme === 'light') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }, []);

  const resolvedTheme = getResolvedTheme(theme);

  const applyTheme = useCallback((resolved: 'light' | 'dark') => {
    const root = document.documentElement;
    const vars = resolved === 'dark' ? darkThemeVars : lightThemeVars;
    Object.entries(vars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    if (resolved === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    root.style.colorScheme = resolved;
  }, []);

  // Применяем тему сразу при изменении theme
  const setTheme = useCallback((newTheme: Theme) => {
    const newResolved = newTheme === 'auto' ? getResolvedTheme(newTheme) : newTheme;
    applyTheme(newResolved); // применяем CSS-переменные и класс до рендера
    setThemeState(newTheme);
  }, [applyTheme, getResolvedTheme]);

  // При монтировании применяем текущую тему
  useLayoutEffect(() => {
    applyTheme(resolvedTheme);
  }, [applyTheme, resolvedTheme]);

  // Сохраняем тему в localStorage при изменении
  useEffect(() => {
    localStorage.setItem('app-theme', theme);
  }, [theme]);

  // Слушатели для auto
  useEffect(() => {
    if (theme !== 'auto') return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const newResolved = getResolvedTheme('auto');
      applyTheme(newResolved);
      // не меняем сам theme, просто обновляем переменные
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, getResolvedTheme, applyTheme]);

  useEffect(() => {
    if (theme !== 'auto') return;
    const tg = (window as any).Telegram?.WebApp;
    if (!tg) return;
    const handler = () => {
      const newResolved = getResolvedTheme('auto');
      applyTheme(newResolved);
    };
    tg.onEvent('themeChanged', handler);
    return () => tg.offEvent('themeChanged', handler);
  }, [theme, getResolvedTheme, applyTheme]);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};